import { useState } from 'react';
import { observer } from 'mobx-react';
import { Drawer, Form, Input, Typography, Button, Space, message } from 'antd';
import { ReadOutlined } from '@ant-design/icons';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import type { Knowledge } from '@/api/types';
import { trackEvent } from '@/lib/telemetry';

const { Text } = Typography;

interface CreateKnowledgeProps {
  open: boolean;
  onClose: () => void;
  onCreated: (created: Knowledge) => void;
}

// Per project convention (CLAUDE.md "Prefer drawers over modals for most
// interactive flows"), the New-KB authoring surface is a right-anchored
// drawer. Two-field form is small, but a drawer keeps the underlying KB
// list visible so the user retains context, and matches the rest of the
// app's authoring patterns. Filename is preserved for compat — callers
// import {CreateKnowledgeModal} but they're getting a drawer now.

export const CreateKnowledgeModal = observer(function CreateKnowledgeModal({
  open,
  onClose,
  onCreated,
}: CreateKnowledgeProps) {
  const { knowledgeStore: kb, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const [form] = Form.useForm<{ title: string; description?: string }>();
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(): Promise<void> {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const created = await kb.create({
        title: values.title.trim(),
        description: values.description?.trim() || undefined,
      });
      trackEvent('Knowledge.Created', { knowledgeId: created.id });
      message.success(`Created "${created.title ?? 'untitled'}"`);
      form.resetFields();
      onCreated(created);
    } catch (err: unknown) {
      const isFormError = err && typeof err === 'object' && 'errorFields' in err;
      if (!isFormError) {
        message.error(err instanceof Error ? err.message : 'Failed to create knowledge base');
      }
    } finally {
      setSubmitting(false);
    }
  }

  function handleClose() {
    form.resetFields();
    onClose();
  }

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      placement="right"
      width={520}
      destroyOnClose
      closable={false}
      title={null}
      styles={{
        body: { padding: 0, display: 'flex', flexDirection: 'column' },
        header: { display: 'none' },
      }}
    >
      {/* Header — same color language as the page hero (ink/violet on slate) */}
      <div
        style={{
          padding: '20px 24px 18px',
          borderBottom: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
          background: isDark
            ? `linear-gradient(135deg, ${colors.ink[700]}33 0%, ${colors.violet[700]}22 100%)`
            : `linear-gradient(135deg, ${colors.ink[50]} 0%, ${colors.violet[50]} 100%)`,
        }}
      >
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <ReadOutlined style={{ fontSize: 18, color: colors.ink[600] }} />
          <Text strong style={{ fontSize: 16 }}>New knowledge base</Text>
        </div>
        <Text type="secondary" style={{ display: 'block', fontSize: 13, lineHeight: 1.55 }}>
          A knowledge base groups PDFs, Markdown documents, and live links that get indexed into
          Azure AI Search. Agents linked to this KB will retrieve relevant chunks at runtime.
        </Text>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
        <Form form={form} layout="vertical" preserve={false} requiredMark="optional">
          <Form.Item
            name="title"
            label="Title"
            rules={[
              { required: true, message: 'A title is required' },
              { max: 300, message: 'Up to 300 characters' },
            ]}
          >
            <Input
              placeholder='e.g. "Diabetes Patient Education"'
              autoFocus
              maxLength={300}
              showCount
            />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            extra="What this KB is for, who it serves, where the content came from."
            rules={[{ max: 500, message: 'Up to 500 characters' }]}
          >
            <Input.TextArea
              placeholder="Optional — short blurb"
              rows={4}
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>
      </div>

      {/* Footer */}
      <div
        style={{
          padding: '12px 24px',
          borderTop: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        }}
      >
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
          <Button type="primary" loading={submitting} onClick={handleSubmit}>
            Create knowledge base
          </Button>
        </Space>
      </div>
    </Drawer>
  );
});
